import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { CheckCircleIcon, BeakerIcon, ClipboardCheckIcon, BookOpenIcon, ArrowRightIcon, BadgeCheckIcon, ShieldCheckIcon, AwardIcon, TrendingUpIcon, FlaskConicalIcon, MicroscopeIcon, UsersIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export const CertificationPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 50
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  const stepVariants = {
    hidden: {
      opacity: 0,
      x: -20
    },
    visible: {
      opacity: 1,
      x: 0
    }
  };
  return <div className="bg-white w-full" data-id="element-365">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-50" data-id="element-366">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-367">
          <div className="text-center" data-id="element-368">
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl" data-id="element-369">
              Ukweli Certified™ Program
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-600" data-id="element-370">
              The world's first three-tier evidence-based certification for
              supplements and functional products
            </p>
          </div>
        </div>
      </section>
      {/* Introduction Section */}
      <section className="py-16 bg-white" data-id="element-371">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-372">
          <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={sectionVariants} transition={{
          duration: 0.8
        }} className="text-center mb-12" data-id="element-373">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-374">
              The Ukweli Certification Process
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto" data-id="element-375">
              Our certification process is designed to move a product from basic
              screening through to clinical-grade validation, giving both
              companies and consumers confidence in safety and efficacy. Each
              level builds upon the previous, creating a comprehensive
              evaluation framework that stands apart in the wellness industry.
            </p>
          </motion.div>
          {/* Process Overview */}
          <div className="mt-12 mb-20" data-id="element-376">
            <div className="relative" data-id="element-377">
              {/* Process Timeline */}
              <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-blue-100" data-id="element-378"></div>
              {/* Step 1 */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={stepVariants} transition={{
              duration: 0.5
            }} className="relative mb-16" data-id="element-379">
                <div className="flex flex-col md:flex-row items-center" data-id="element-380">
                  <div className="md:w-1/2 md:pr-12 mb-8 md:mb-0" data-id="element-381">
                    <div className="bg-blue-50 p-6 rounded-lg shadow-sm" data-id="element-382">
                      <div className="flex items-center mb-4" data-id="element-383">
                        <div className="bg-blue-100 p-2 rounded-full mr-3" data-id="element-384">
                          <ClipboardCheckIcon size={24} className="text-blue-600" data-id="element-385" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900" data-id="element-386">
                          Step 1: Application & Intake
                        </h3>
                      </div>
                      <ul className="space-y-3 text-gray-700" data-id="element-387">
                        <li className="flex items-start" data-id="element-388">
                          <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-389" />
                          <span data-id="element-390">
                            Supplement, probiotic, or nutraceutical companies
                            apply to have their product evaluated.
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-391">
                          <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-392" />
                          <span data-id="element-393">
                            The Institute reviews product dossiers (ingredients,
                            claims, manufacturing details).
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-394">
                          <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-395" />
                          <span data-id="element-396">
                            A certification pathway (Level 1, 2, or 3) is
                            selected depending on the company's needs and
                            evidence available.
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white relative z-10" data-id="element-397">
                    <span className="text-lg font-bold" data-id="element-398">1</span>
                  </div>
                  <div className="md:w-1/2 md:pl-12" data-id="element-399">
                    <img src="https://images.unsplash.com/photo-1579621970795-87facc2f976d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80" alt="Application process" className="rounded-lg shadow-md w-full h-auto" data-id="element-400" />
                  </div>
                </div>
              </motion.div>
              {/* Step 2 */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={stepVariants} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="relative mb-16" data-id="element-401">
                <div className="flex flex-col md:flex-row-reverse items-center" data-id="element-402">
                  <div className="md:w-1/2 md:pl-12 mb-8 md:mb-0" data-id="element-403">
                    <div className="bg-blue-50 p-6 rounded-lg shadow-sm" data-id="element-404">
                      <div className="flex items-center mb-4" data-id="element-405">
                        <div className="bg-blue-100 p-2 rounded-full mr-3" data-id="element-406">
                          <ShieldCheckIcon size={24} className="text-blue-600" data-id="element-407" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900" data-id="element-408">
                          Step 2: Level 1 – Evidence Screen
                        </h3>
                      </div>
                      <p className="text-blue-600 font-semibold mb-3" data-id="element-409">
                        Cost: $7,000
                      </p>
                      <div className="mb-4" data-id="element-410">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-411">
                          Tests conducted:
                        </h4>
                        <ul className="space-y-2 text-gray-700" data-id="element-412">
                          <li className="flex items-start" data-id="element-413">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-414" />
                            <span data-id="element-415">Purity & safety compliance.</span>
                          </li>
                          <li className="flex items-start" data-id="element-416">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-417" />
                            <span data-id="element-418">
                              Verification of label claims (dosage accuracy,
                              absence of contaminants or adulterants).
                            </span>
                          </li>
                        </ul>
                      </div>
                      <div data-id="element-419">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-420">
                          Outcome:
                        </h4>
                        <ul className="space-y-2 text-gray-700" data-id="element-421">
                          <li className="flex items-start" data-id="element-422">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-423" />
                            <span data-id="element-424">
                              If compliant, product earns Ukweli Certified™ –
                              Level 1.
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-425">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-426" />
                            <span data-id="element-427">
                              Report is published in an accessible, transparent
                              format for consumers and professionals.
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white relative z-10" data-id="element-428">
                    <span className="text-lg font-bold" data-id="element-429">2</span>
                  </div>
                  <div className="md:w-1/2 md:pr-12" data-id="element-430">
                    <div className="p-6 bg-white rounded-lg shadow-md border border-gray-200" data-id="element-431">
                      <div className="flex justify-between items-start mb-6" data-id="element-432">
                        <div className="flex items-center" data-id="element-433">
                          <BadgeCheckIcon size={32} className="text-blue-600 mr-3" data-id="element-434" />
                          <h4 className="text-xl font-bold text-gray-900" data-id="element-435">
                            Level 1 Certification
                          </h4>
                        </div>
                        <div className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full" data-id="element-436">
                          Basic Verification
                        </div>
                      </div>
                      <div className="mb-6" data-id="element-437">
                        <img src="https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="Level 1 Certification" className="w-full h-40 object-cover rounded-md mb-4" data-id="element-438" />
                        <div className="flex justify-center" data-id="element-439">
                          <div className="bg-blue-100 rounded-full p-3" data-id="element-440">
                            <ShieldCheckIcon size={40} className="text-blue-600" data-id="element-441" />
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-700 text-center" data-id="element-442">
                        Verifies basic safety and label accuracy, ensuring the
                        product contains what it claims and is free from harmful
                        contaminants.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
              {/* Step 3 */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={stepVariants} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="relative mb-16" data-id="element-443">
                <div className="flex flex-col md:flex-row items-center" data-id="element-444">
                  <div className="md:w-1/2 md:pr-12 mb-8 md:mb-0" data-id="element-445">
                    <div className="bg-blue-50 p-6 rounded-lg shadow-sm" data-id="element-446">
                      <div className="flex items-center mb-4" data-id="element-447">
                        <div className="bg-blue-100 p-2 rounded-full mr-3" data-id="element-448">
                          <FlaskConicalIcon size={24} className="text-blue-600" data-id="element-449" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900" data-id="element-450">
                          Step 3: Level 2 – Mechanistic Validation
                        </h3>
                      </div>
                      <p className="text-blue-600 font-semibold mb-3" data-id="element-451">
                        Cost: $14,000
                      </p>
                      <div className="mb-4" data-id="element-452">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-453">
                          Tests conducted:
                        </h4>
                        <ul className="space-y-2 text-gray-700" data-id="element-454">
                          <li className="flex items-start" data-id="element-455">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-456" />
                            <span data-id="element-457">
                              In vitro lab assays (e.g., biofilm disruption,
                              cell culture immune response).
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-458">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-459" />
                            <span data-id="element-460">
                              Biomarker testing (PKR pathway, cytokine
                              modulation, metabolic activity).
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-461">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-462" />
                            <span data-id="element-463">
                              Evidence that the compound produces biologically
                              plausible effects.
                            </span>
                          </li>
                        </ul>
                      </div>
                      <div data-id="element-464">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-465">
                          Outcome:
                        </h4>
                        <ul className="space-y-2 text-gray-700" data-id="element-466">
                          <li className="flex items-start" data-id="element-467">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-468" />
                            <span data-id="element-469">
                              If confirmed, product earns Ukweli Certified™ –
                              Level 2.
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-470">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-471" />
                            <span data-id="element-472">
                              Certificate highlights mechanistic evidence and
                              product's scientific backing.
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white relative z-10" data-id="element-473">
                    <span className="text-lg font-bold" data-id="element-474">3</span>
                  </div>
                  <div className="md:w-1/2 md:pl-12" data-id="element-475">
                    <div className="p-6 bg-white rounded-lg shadow-md border border-gray-200" data-id="element-476">
                      <div className="flex justify-between items-start mb-6" data-id="element-477">
                        <div className="flex items-center" data-id="element-478">
                          <AwardIcon size={32} className="text-blue-600 mr-3" data-id="element-479" />
                          <h4 className="text-xl font-bold text-gray-900" data-id="element-480">
                            Level 2 Certification
                          </h4>
                        </div>
                        <div className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full" data-id="element-481">
                          Efficacy Evidence
                        </div>
                      </div>
                      <div className="mb-6" data-id="element-482">
                        <img src="https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="Level 2 Certification" className="w-full h-40 object-cover rounded-md mb-4" data-id="element-483" />
                        <div className="flex justify-center" data-id="element-484">
                          <div className="bg-blue-100 rounded-full p-3" data-id="element-485">
                            <MicroscopeIcon size={40} className="text-blue-600" data-id="element-486" />
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-700 text-center" data-id="element-487">
                        Confirms that the product demonstrates measurable
                        biological activity in laboratory testing, validating
                        its mechanism of action.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
              {/* Step 4 */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={stepVariants} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="relative mb-16" data-id="element-488">
                <div className="flex flex-col md:flex-row-reverse items-center" data-id="element-489">
                  <div className="md:w-1/2 md:pl-12 mb-8 md:mb-0" data-id="element-490">
                    <div className="bg-blue-50 p-6 rounded-lg shadow-sm" data-id="element-491">
                      <div className="flex items-center mb-4" data-id="element-492">
                        <div className="bg-blue-100 p-2 rounded-full mr-3" data-id="element-493">
                          <div size={24} className="text-blue-600" data-id="element-494" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900" data-id="element-495">
                          Step 4: Level 3 – Clinical Validation
                        </h3>
                      </div>
                      <p className="text-blue-600 font-semibold mb-3" data-id="element-496">
                        Cost: $28,000
                      </p>
                      <div className="mb-4" data-id="element-497">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-498">
                          Tests conducted:
                        </h4>
                        <ul className="space-y-2 text-gray-700" data-id="element-499">
                          <li className="flex items-start" data-id="element-500">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-501" />
                            <span data-id="element-502">
                              Human clinical trial data reviewed by an
                              independent scientific panel.
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-503">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-504" />
                            <span data-id="element-505">
                              Randomized pilot or larger-scale trials assessing
                              tolerability, efficacy, and outcomes.
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-506">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-507" />
                            <span data-id="element-508">
                              Measures include microbiome diversity, barrier
                              integrity, inflammation markers, and
                              patient-reported results.
                            </span>
                          </li>
                        </ul>
                      </div>
                      <div data-id="element-509">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-510">
                          Outcome:
                        </h4>
                        <ul className="space-y-2 text-gray-700" data-id="element-511">
                          <li className="flex items-start" data-id="element-512">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-513" />
                            <span data-id="element-514">
                              If validated, product earns Ukweli Certified™ –
                              Level 3 — the highest seal of credibility.
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-515">
                            <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-516" />
                            <span data-id="element-517">
                              Report is peer-reviewed style and publicly
                              accessible.
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white relative z-10" data-id="element-518">
                    <span className="text-lg font-bold" data-id="element-519">4</span>
                  </div>
                  <div className="md:w-1/2 md:pr-12" data-id="element-520">
                    <div className="p-6 bg-white rounded-lg shadow-md border border-gray-200" data-id="element-521">
                      <div className="flex justify-between items-start mb-6" data-id="element-522">
                        <div className="flex items-center" data-id="element-523">
                          <TrendingUpIcon size={32} className="text-blue-600 mr-3" data-id="element-524" />
                          <h4 className="text-xl font-bold text-gray-900" data-id="element-525">
                            Level 3 Certification
                          </h4>
                        </div>
                        <div className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full" data-id="element-526">
                          Clinical Validation
                        </div>
                      </div>
                      <div className="mb-6" data-id="element-527">
                        <img src="https://images.unsplash.com/photo-1576086213369-97a306d36557?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="Level 3 Certification" className="w-full h-40 object-cover rounded-md mb-4" data-id="element-528" />
                        <div className="flex justify-center" data-id="element-529">
                          <div className="bg-blue-100 rounded-full p-3" data-id="element-530">
                            <UsersIcon size={40} className="text-blue-600" data-id="element-531" />
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-700 text-center" data-id="element-532">
                        Provides the highest level of validation through human
                        clinical trials, demonstrating real-world effectiveness
                        and safety.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
              {/* Step 5 */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={stepVariants} transition={{
              duration: 0.5,
              delay: 0.4
            }} className="relative" data-id="element-533">
                <div className="flex flex-col md:flex-row items-center" data-id="element-534">
                  <div className="md:w-1/2 md:pr-12 mb-8 md:mb-0" data-id="element-535">
                    <div className="bg-blue-50 p-6 rounded-lg shadow-sm" data-id="element-536">
                      <div className="flex items-center mb-4" data-id="element-537">
                        <div className="bg-blue-100 p-2 rounded-full mr-3" data-id="element-538">
                          <BookOpenIcon size={24} className="text-blue-600" data-id="element-539" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900" data-id="element-540">
                          Step 5: Publication & Consumer Access
                        </h3>
                      </div>
                      <ul className="space-y-3 text-gray-700" data-id="element-541">
                        <li className="flex items-start" data-id="element-542">
                          <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-543" />
                          <span data-id="element-544">
                            Results from all three levels are summarized in
                            open-access reports.
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-545">
                          <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-546" />
                          <span data-id="element-547">
                            Certified products can display the Ukweli
                            Certified™ seal on packaging and marketing.
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-548">
                          <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-549" />
                          <span data-id="element-550">
                            Consumers, clinicians, and regulators can reference
                            the certification database to identify trusted,
                            evidence-based products.
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white relative z-10" data-id="element-551">
                    <span className="text-lg font-bold" data-id="element-552">5</span>
                  </div>
                  <div className="md:w-1/2 md:pl-12" data-id="element-553">
                    <img src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80" alt="Publication and consumer access" className="rounded-lg shadow-md w-full h-auto" data-id="element-554" />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      {/* Why This Matters Section */}
      <section className="py-16 bg-gray-50" data-id="element-555">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-556">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-557">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-558">
              <span className="inline-block bg-blue-600 text-white rounded-full h-10 w-10 flex items-center justify-center mr-3" data-id="element-559">
                🎯
              </span>
              Why This Matters
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-560">
              The Ukweli Certification process creates value across the entire
              wellness ecosystem
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-561">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-white p-8 rounded-lg shadow-md" data-id="element-562">
              <div className="flex items-center mb-4" data-id="element-563">
                <div className="bg-blue-100 p-3 rounded-full mr-3" data-id="element-564">
                  <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-565">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" data-id="element-566" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900" data-id="element-567">
                  For Companies
                </h3>
              </div>
              <p className="text-gray-700" data-id="element-568">
                Certification provides a competitive edge and consumer trust. In
                a crowded marketplace, the Ukweli Certified™ seal
                differentiates products backed by real science, building brand
                credibility and opening doors to discerning consumers and
                healthcare providers.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-white p-8 rounded-lg shadow-md" data-id="element-569">
              <div className="flex items-center mb-4" data-id="element-570">
                <div className="bg-blue-100 p-3 rounded-full mr-3" data-id="element-571">
                  <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-572">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" data-id="element-573" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900" data-id="element-574">
                  For Consumers
                </h3>
              </div>
              <p className="text-gray-700" data-id="element-575">
                The seal ensures truth you can trust in a crowded wellness
                market. Consumers can make informed decisions based on
                transparent, independent testing rather than marketing claims,
                leading to better health outcomes and more effective use of
                wellness products.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="bg-white p-8 rounded-lg shadow-md" data-id="element-576">
              <div className="flex items-center mb-4" data-id="element-577">
                <div className="bg-blue-100 p-3 rounded-full mr-3" data-id="element-578">
                  <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-579">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" data-id="element-580" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900" data-id="element-581">For Science</h3>
              </div>
              <p className="text-gray-700" data-id="element-582">
                Each certification contributes to the global body of
                transparent, reproducible evidence. By creating standardized
                testing protocols and open-access data, the Ukweli Institute
                elevates the entire field of wellness science and creates a
                foundation for future research.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-white" data-id="element-583">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-584">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-blue-50 rounded-xl p-8 md:p-12 text-center" data-id="element-585">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-586">
              Ready to Get Certified?
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8" data-id="element-587">
              Join the growing community of evidence-based wellness products
              that stand apart with the Ukweli Certified™ seal. Our team is
              ready to guide you through the certification process.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-588">
              <Link to="/contact" className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-md hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center" data-id="element-589">
                Apply for Certification{' '}
                <ArrowRightIcon size={16} className="ml-2" data-id="element-590" />
              </Link>
              <Link to="/research" className="px-6 py-3 bg-white text-blue-600 font-medium rounded-md shadow-md hover:bg-gray-100 transition-colors duration-300" data-id="element-591">
                Learn About Our Research
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
};